import { OrderDetailPage, Remark, usePDFPrintHook } from "@careinfo/layout";
import { ClientAccessControl } from "@careinfo/permission-required";
import { tw } from "@careinfo/tailwind";
import { ColumnDef } from "@tanstack/react-table";
import moment from "moment";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import APIS from "../../constant/Endpoint";
import DataTable from "../Reusable/DataTable";
import Modal from "../Reusable/Modal";
import TableActionButton from "../Reusable/TableActionButton";

const OrderList = (props: any) => {
  const [searchParams, setSearchParms] = useSearchParams();
  const checkedAllItems = searchParams.get("selectAllItems") === "1";
  const { filterParams } = props;
  const { downloadPrint, isPrintLoading } = usePDFPrintHook();

  const [openReviewModal, setOpenReviewModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState("");
  const [openHistoryPopup, setOpenHistoryPopup] = useState(false);

  const handlePDFPrint = async () => {
    const selectedOrders = searchParams.get("selectedItems")?.split(",");
    if (!selectedOrders) {
      toast.error("Please select at least one order first");
      return;
    }
    const url = `${APIS.ORDER}order-report/`;
    const formData = {
      order_ids: selectedOrders,
    };
    try {
      await downloadPrint(url, formData);
    } catch (err) {
      console.error(err);
    }
  };

  const handleIndividualSelect = (value: string) => {
    let selectedItems = searchParams.get("selectedItems")?.split(",");
    if (!selectedItems || selectedItems === undefined) {
      searchParams.set("selectedItems", value);
      setSearchParms(searchParams);
      return;
    }
    let itemIndex = selectedItems?.findIndex((item) => item === value);
    if (itemIndex > -1) {
      selectedItems.splice(itemIndex, 1);
      const newSelectedItems = selectedItems
        ?.map((item) => item)
        .join(",")
        .toString();
      if (newSelectedItems.length) {
        searchParams.set("selectedItems", newSelectedItems);
      } else {
        searchParams.delete("selectedItems");
      }
      searchParams.set("selectAllItems", "0");
      setSearchParms(searchParams);
    } else {
      selectedItems?.push(value);
      const newSelectedItems = selectedItems
        ?.map((item) => item)
        .join(",")
        .toString();
      searchParams.set("selectedItems", newSelectedItems);
      setSearchParms(searchParams);
    }
  };

  const isSelected = (value: string) => {
    const selectedItems = searchParams.get("selectedItems")?.split(",");
    if (!selectedItems) {
      return false;
    }
    return selectedItems.includes(value);
  };

  const handleSelectAll = (table: any) => {
    const {
      options: { data: tableData },
    } = table;
    const isSelected = searchParams.get("selectAllItems");
    if (isSelected === "1") {
      searchParams.set("selectAllItems", "0");
      searchParams.delete("selectedItems");
      setSearchParms(searchParams);
    } else {
      const selectedItems = tableData
        ?.map((item: any) => item.id)
        .join(",")
        .toString();
      searchParams.set("selectAllItems", "1");
      searchParams.set("selectedItems", selectedItems);
      setSearchParms(searchParams);
    }
  };

  const defaultColumns: ColumnDef<any, any>[] = [
    {
      id: "select",
      header: ({ table }) => {
        return (
          <input
            type="checkbox"
            checked={checkedAllItems}
            onChange={() => handleSelectAll(table)}
            className={tw`cursor-pointer`}
          />
        );
      },
      cell: ({ row }) => (
        <input
          type="checkbox"
          checked={isSelected(row?.original?.id)}
          onChange={(e) => {
            handleIndividualSelect(row?.original?.id);
          }}
          className={tw`cursor-pointer`}
        />
      ),
    },
    {
      header: "WayBill",
      accessorKey: "tracking_number",
      cell: ({ row, getValue }: any) => {
        return (
          <button
            onClick={() => {
              setSelectedOrder(row?.original?.id);
              setOpenHistoryPopup(true);
            }}
            className={tw`cursor-pointer focus:outline-none px-4 relative`}
          >
            <ClientAccessControl allowedPermissions={["can_view_order_id"]}>
              <p
                className={tw`text-xs text-gray-500 text-center whitespace-nowrap`}
              >
                {row?.original?.order_number}
              </p>
            </ClientAccessControl>
            <ClientAccessControl
              children={
                <p className={tw`whitespace-nowrap text-center`}>
                  {getValue() ?? "N/A"}
                </p>
              }
              allowedPermissions={["can_view_tracking"]}
            />
            <p
              className={tw`text-xs text-gray-500 text-center whitespace-nowrap`}
            >
              {moment(row?.original?.created).format("YY-MM-DD HH:mm:ss")}
            </p>
            <ClientAccessControl
              children={
                <p className={tw`text-xs text-gray-500 uppercase text-center`}>
                  {row?.original?.service_type?.name}
                </p>
              }
              allowedPermissions={["can_view_service_type"]}
            />
            {row?.original?.is_pickup_created && (
              <span
                className={tw` absolute -top-2 text-[10px] -right-1 text-white px-[4px] bg-red-400 rounded-full `}
              >
                P
              </span>
            )}
          </button>
        );
      },
    },
    {
      header: "Origin",
      accessorKey: "pickup_branch.name",
      cell: ({ row, getValue }) => {
        return (
          <>
            <p className={tw`uppercase`}>{getValue()}</p>
            <p className={tw`text-xs text-primary`}>
              {row?.original?.pickup_branch?.warehouse?.name}
            </p>
          </>
        );
      },
    },
    {
      header: "Sender",
      accessorKey: "sender.full_name",
      cell: ({ row }: any) => {
        return (
          <>
            <ClientAccessControl
              children={
                <p className={tw``}>
                  {row?.original?.sender?.full_name ?? "N/A"}{" "}
                </p>
              }
              allowedPermissions={["can_view_sender_name"]}
            />
            <ClientAccessControl
              children={
                <p className={tw`whitespace-nowrap text-sm`}>
                  {row?.original?.sender?.primary_contact ?? "N/A"}{" "}
                </p>
              }
              allowedPermissions={["can_view_sender_number"]}
            />
          </>
        );
      },
    },
    {
      header: "Receiver",
      accessorKey: "receiver.full_name",
      cell: ({ row }: any) => {
        return (
          <>
            <ClientAccessControl
              children={
                <p className={tw``}>
                  {row?.original?.receiver.full_name ?? "N/A"}{" "}
                </p>
              }
              allowedPermissions={["can_view_receiver_name"]}
            />
            <ClientAccessControl
              children={
                <p className={tw`whitespace-nowrap text-sm`}>
                  {row?.original?.receiver.primary_contact ?? "N/A"}{" "}
                </p>
              }
              allowedPermissions={["can_view_receiver_number"]}
            />
            <ClientAccessControl
              children={
                <p className={tw`text-sm`}>
                  {row?.original?.receiver.address ?? "N/A"}{" "}
                </p>
              }
              allowedPermissions={["can_view_receiver_location"]}
            />
          </>
        );
      },
    },
    {
      header: "Destination",
      accessorKey: "destination.name",
      cell: ({ row, getValue }) => {
        return (
          <>
            <p className={tw`uppercase`}>{getValue()}</p>
            <p className={tw`text-xs text-primary`}>
              {row?.original?.destination_branch?.warehouse?.name}
            </p>
          </>
        );
      },
    },
    {
      header: "Pieces",
      accessorKey: "piece",
      cell: ({ getValue }: any) => {
        return (
          <>
            <ClientAccessControl
              children={
                <p>
                  {getValue()}
                  <span className="text-gray-500 text-xs">pieces</span>{" "}
                </p>
              }
              allowedPermissions={["can_view_piece"]}
            />
          </>
        );
      },
    },
    {
      header: "Remarks",
      accessorKey: "remark",
      cell: ({ row, getValue }: any) => (
        <>
          <ClientAccessControl
            children={
              <button
                className={tw`focus:outline-none`}
                onClick={() => {
                  setSelectedOrder(row?.original?.id);
                  setOpenReviewModal(true);
                }}
              >
                {getValue()} Remarks
              </button>
            }
            allowedPermissions={["can_view_remarks"]}
            renderNoAccess={<p>{getValue()} Remarks</p>}
          />
        </>
      ),
    },
    {
      header: "Status",
      accessorKey: "delivery_status.name",
      cell: ({ row, getValue }: any) => {
        return (
          <>
            <ClientAccessControl
              children={
                <>
                  <p
                    className={tw`uppercase px-1 text-center rounded-md`}
                    style={{
                      background: row?.original?.delivery_status?.color,
                    }}
                  >
                    {getValue()}
                  </p>
                  <p className={tw`text-xs text-primary `}>
                    {row?.original?.rider?.name}
                  </p>
                </>
              }
              allowedPermissions={["can_view_status"]}
            />
          </>
        );
      },
    },
    {
      header: "Action",
      accessorKey: "id",
      cell: ({ row, getValue }: any) => (
        <>
          <ClientAccessControl allowedPermissions={["can_view_order_edit"]}>
            <TableActionButton
              editPath={`order?limit=10&page=0#/edit/${getValue()}`}
            />
          </ClientAccessControl>
        </>
      ),
    },
  ];

  return (
    <>
      <DataTable
        url={APIS.ORDER}
        queryKey={"orders"}
        columns={defaultColumns}
        tableName={"consignment"}
        isUpload={true}
        isDownload={true}
        params={{
          ...filterParams,
          is_rtv: filterParams?.is_rtv ? true : null,
          order_remarks__isnull: filterParams?.order_remarks__isnull
            ? false
            : null,
          is_walking_customer: filterParams?.is_walking_customer ? true : null,
        }}
        actionMenu={
          <div className={tw`flex items-center justify-end w-full`}>
            <button
              className={tw`px-2 border border-primary rounded-md py-1 max-w-32 focus:outline-none`}
              onClick={handlePDFPrint}
              disabled={isPrintLoading}
            >
              Order Report
            </button>
          </div>
        }
      />
      <Modal
        isOpen={openHistoryPopup}
        setIsOpen={setOpenHistoryPopup}
        title={"Order Detail"}
        children={
          <>
            <OrderDetailPage selectedOrder={selectedOrder} />
          </>
        }
      />
      <Modal
        isOpen={openReviewModal}
        setIsOpen={setOpenReviewModal}
        title={"Remark"}
        children={
          <>
            <Remark selectedOrder={selectedOrder} />
          </>
        }
      />
    </>
  );
};

export default OrderList;
